import { useState, useEffect, useCallback, useMemo } from "react";

import { useNavigate } from "react-router-dom";

//Props Validations
import PropTypes from "prop-types";

// import component
import Loader from "../../common/Loader";
import FilterAcrossProductsToggleComponent from "../../common/FilterAcrossProductsToggleComponent";
import Reset from "../../common/Reset";
import TTestDialog from "../../common/TTest/TTestDialog";
import AnalysisTypeSelector from "../../common/AnalysisTypeSelector";
import ViewToggle from "../../common/ViewToggle";
import InitialPopup from "../../common/InitialPopup";
import CohensDButton from "components/common/CohensDButton";
import SignificanceToggleComponent from "components/common/SignificanceToggleComponent";
import AnalysisTableTransposed from "../../common/analysisModule/AnalysisTableTransposed";
import AnalysisTransposedTableChart from "../../common/analysisModule/chart/AnalysisTransposedTableChart";
import DataPointSampling from "components/common/DataPointSampling";
import ScoreDistributionVariableSelect from "components/common/ScoreDistributionVariableSelect";

// redux
import { useSelector, useDispatch } from "react-redux";
import {
    loadIsFragranceMetricsAnalysisResultLoadingState,
    loadFragranceMetricsAnalysisResultState,
    loadDefaultFragranceMetricsAnalysisResultState,
    loadDisableSignificanceToggleState,
    loadSignificanceToggleState,
    loadIsScoreDistributionShownAdditionalState,
    loadScoreDistributionSelectedVariableAdditionalState,
    loadScoreDistributionGroupsAdditionalState,
} from "store/features/analysisResultsSlice";

import { resetTTestStore } from "store/features/ttestSlice";

// utils
import GetFragranceMetricsResult from "utils/apis/GetFragranceMetricsResult";
import { loadSecondaryRemovedKpiListState } from "store/features/analysisKpiSlice";

function AditionalAttributeTab({ dataMappingData = {},pageContext = "" }) {
    const navigate = useNavigate();

    //REDUX
    const dispatch = useDispatch();
    const selectedStudy = useSelector((state) => state.studyDataStore.selectedStudies);

    const productsSecondaryDataState = useSelector((state) => state.analysisResultsStore.fragranceMetricsAnalysisResult);
    const isLoading = useSelector((state) => state.analysisResultsStore.isFragranceMetricsAnalysisResultLoading);
    const weightToggle = useSelector((state) => state.analysisResultsStore.weightToggle);
    const isConsumerFilterSelected = useSelector((state) => state.consumerFilterStore.filterSelectionStatus);

    const productsOrder = useSelector((state) => state.productsInfoStore.productsOrder);
    const productCategoryData = useSelector((state) => state.settingsStore.productCategory);
    const productCategoryId = productCategoryData?.value || null;
    const isUnileverFragranceHouse = process.env?.REACT_APP_UNILEVER_FRAGRANCE_HOUSE?.toLowerCase() === productCategoryData?.label?.toLowerCase();

    // toggle table and chart
    const [isTableShown, setIsTableShown] = useState(true);

    // t-test details
    const [tTestResult, setTTestResult] = useState([]);
    const [testLabel, setTestLabel] = useState("");

    // Analysis type
    const [isFisher, setIsFisher] = useState(false);
    const [legends, setLegends] = useState([]);

    // cohens d details
    const [cohensDResult, setCohensDResult] = useState([]);

    // data for data point sampling
    const [data, setData] = useState({});

    const scoreDistributionSelectedVariableAdditional = useSelector((state) => state.analysisResultsStore.scoreDistributionSelectedVariableAdditional);
    const isScoreDistributionShownAdditional = useSelector((state) => state.analysisResultsStore.isScoreDistributionShownAdditional);
    const scoreDistributionGroupsAdditional = useSelector((state) => state.analysisResultsStore.scoreDistributionGroupsAdditional);
    const likertScaleData = useSelector((state) => state.analysisResultsStore.likertScaleData);
    const hiddenVariable = isUnileverFragranceHouse ? ["R_OOF"] : ["R_OO_Frag_10pt"]

    const isOralCare = process.env?.REACT_APP_ORAL_CARE?.toLowerCase() === productCategoryData?.label?.toLowerCase();
    const isSkinCleansing = process.env?.REACT_APP_SKIN_CLEANSING?.toLowerCase() === productCategoryData?.label?.toLowerCase();

    // box analysis code
    const overallBoxAnalysisSelection = useSelector((state) => state.analysisTypeSelectionStore.overallBoxAnalysisSelection);
    const secondaryBoxAnalysisSelection = useSelector((state) => state.analysisTypeSelectionStore.secondaryBoxAnalysisSelection);
    const agreementBoxAnalysisSelection = useSelector((state) => state.analysisTypeSelectionStore.agreementBoxAnalysisSelection);
    const claimsBoxAnalysisSelection = useSelector((state) => state.analysisTypeSelectionStore.claimsBoxAnalysisSelection);
    const fragranceAdditionalBoxAnalysisSelection = useSelector((state) => state.analysisTypeSelectionStore.fragranceAdditionalBoxAnalysisSelection);
    const analysisTypeValue = useSelector((state) => state.analysisTypeSelectionStore.fragranceAdditionalTypeValue);
    const boxAnalysisTypeSelection = useSelector((state) => state.analysisTypeSelectionStore.fragranceAdditionalBoxAnalysisTypeSelection);
    const moduleName = "secondary";
    // const [activeTab, setActiveTab] = useState("");
    const categoriesToDisplay = isUnileverFragranceHouse ? ["Overall Opinion", "Fragrance Characteristics", "Fragrance Fitment"] : ["Overall Fragrance", "Fragrance (Secondary)", "Fragrance (JAR)"];
    const macroCategoryData = dataMappingData?.columns ?? [];
    // % Score Distribution view is available only for Unilever Fragrance House
    const isScoreDistributionMode = isUnileverFragranceHouse && !isTableShown && !isScoreDistributionShownAdditional && selectedStudy.length === 1 && analysisTypeValue.value === "mean";
    const sortedItems = useMemo(() => {
        const orderIndex = new Map(
            categoriesToDisplay.map((k, i) => [k, i])
        );

        return (macroCategoryData ?? [])
            .slice()
            .sort((a, b) => {
                const ia = orderIndex.get(a.macroCategory) ?? Infinity;
                const ib = orderIndex.get(b.macroCategory) ?? Infinity;
                return ia - ib;
            })
            .map(category => ({
                ...category,
                displayLabels: category.displayLabels
                    ?.slice()
                    .sort((a, b) =>
                        a.displayLabel.localeCompare(b.displayLabel)
                    )
            }));
    }, [macroCategoryData]);

    const clearResult = useCallback(() => {
        setTTestResult([]);
        setTestLabel("");
        dispatch(resetTTestStore());
        dispatch(loadSignificanceToggleState(false));
        dispatch(loadDisableSignificanceToggleState(true));
        setCohensDResult([]);
        dispatch(loadSecondaryRemovedKpiListState(hiddenVariable));
        if (isUnileverFragranceHouse) {
            dispatch(loadScoreDistributionSelectedVariableAdditionalState(""));
            dispatch(loadScoreDistributionGroupsAdditionalState([]));
            dispatch(loadIsScoreDistributionShownAdditionalState(true));
            setData({});
        }
        // onTTestDataReceived(false, "", "", "", null, "");
    }, [isUnileverFragranceHouse]);

    const clearResultForBoxAnalysis = useCallback(() => {
        setTTestResult([]);
        setTestLabel("");
        dispatch(resetTTestStore());
        dispatch(loadSignificanceToggleState(false));
        dispatch(loadDisableSignificanceToggleState(true));
        setCohensDResult([]);
        if (isUnileverFragranceHouse) {
            dispatch(loadScoreDistributionSelectedVariableAdditionalState(""));
            dispatch(loadScoreDistributionGroupsAdditionalState([]));
            dispatch(loadIsScoreDistributionShownAdditionalState(true));
            setData({});
        }
        // onTTestDataReceived(false, "", "", "", null, "");
    }, [isUnileverFragranceHouse]);

    // clear T Test
    useEffect(() => {
        clearResultForBoxAnalysis();
    }, [weightToggle]);

    useEffect(() => {
        if (isOralCare || isSkinCleansing) {
            if (!isLoading) {
                if (!isConsumerFilterSelected) {
                    InitialPopup.general();
                }
            }
        }
    }, [isLoading, isConsumerFilterSelected, isOralCare, isSkinCleansing]);

    useEffect(() => {
        dispatch(loadSecondaryRemovedKpiListState(hiddenVariable));
    }, []);

    const clearTtest = (product, type, isAdditional) => {
        const clearResults = isAdditional ? clearResultForBoxAnalysis : clearResult;
        // clear t test
        if (type === "consumerFilterAll") {
            clearResults();
        } else if (type === "consumerFilter") {
            const tTestPro = tTestResult?.[0]?.colors?.map((datum) => datum.productCode) || [];
            const cfPc = product?.split("_")?.join(" ") || "";
            if (tTestPro.indexOf(cfPc) !== -1) {
                clearResults();
            }
        } else {
            clearResults();
        }
    };

    const getAditionalResult = async ({ product = null, coreDataId = null, type = null, consumerFilter = null, pcs = [], selectedStudy = [], allAnalysisData = {}, fetchOnlyCurrent = false, boxAnalysisSelection = {}, isAdditional = false }) => {
        clearTtest(product, type, isAdditional);
        dispatch(loadIsFragranceMetricsAnalysisResultLoadingState(true));

        const fragranceMetricsResult = await GetFragranceMetricsResult({
            product,
            coreDataId,
            type,
            consumerFilter,
            pcs,
            selectedStudy,
            productsSecondaryDataState: allAnalysisData?.fragranceMetricsAnalysisResult || [],
            productsOrder,
            productCategoryId,
            boxAnalysisSelection: boxAnalysisSelection["fragranceAdditional"] || {},
            studyTypeMethod: "clt",
        });
        if (fragranceMetricsResult) {
            await dispatch(loadFragranceMetricsAnalysisResultState([...fragranceMetricsResult]));
            if (!type) {
                dispatch(loadDefaultFragranceMetricsAnalysisResultState([...fragranceMetricsResult]));
            }
        }
        dispatch(loadIsFragranceMetricsAnalysisResultLoadingState(false));
        console.log("fetchOnlyCurrent", fetchOnlyCurrent);
    };

    useEffect(() => {
        async function fetchAnalysisResult() {
            // if (!productsSecondaryDataState.length) {
                await getAditionalResult({
                    product: null,
                    coreDataId: null,
                    type: null,
                    consumerFilter: null,
                    pcs: [],
                    selectedStudy,
                    allAnalysisData: [],
                    fetchOnlyCurrent: true,
                    boxAnalysisSelection: {
                        overall: overallBoxAnalysisSelection,
                        secondary: secondaryBoxAnalysisSelection,
                        agreement: agreementBoxAnalysisSelection,
                        claims: claimsBoxAnalysisSelection,
                        fragranceAdditional: fragranceAdditionalBoxAnalysisSelection,
                    },
                });
            // }
        }

        if (selectedStudy.length === 0) {
            navigate("/studies");
        } else {
            fetchAnalysisResult();
        }
    }, [selectedStudy]);
    const isJarAnalysis = productsSecondaryDataState?.some(data => data.scores?.some(datum => datum.backendVariable?.toUpperCase().includes("(JAR)"))) || false;

    return (
        <>
            {isLoading && <Loader />}
            <div
                className="card-btns"
                style={{ padding: "0px 0px", margin: "10px 0px" }}
            >
                <div
                    className=" d-flex"
                    style={{ alignItems: "center" }}
                >
                    <div>
                        <ViewToggle
                            value={isTableShown}
                            setValue={setIsTableShown}
                            labels={["Table", "Chart"]}
                        />
                    </div>
                    <div
                        style={{
                            marginLeft: "1.5rem",
                            zIndex: "3",
                            width: "150px",
                        }}
                    >
                        <AnalysisTypeSelector
                            value={analysisTypeValue}
                            pageName="fragranceAdditionalAnalysis"
                            variableCategory={"Fragrance Metrics Analysis"}
                            handleFilterSubmit={getAditionalResult}
                            mappings={dataMappingData.columns}
                            moduleName="secondary"
                            clearResult={clearResultForBoxAnalysis}
                            disabled={isScoreDistributionMode}
                        />
                    </div>
                    {isScoreDistributionMode && (
                        <>
                            <div
                                style={{
                                    marginLeft: "1.5rem",
                                    zIndex: "3",
                                    width: "150px",
                                }}
                            >
                                <ScoreDistributionVariableSelect
                                    dataMappingData={dataMappingData?.columns?.filter((col) => categoriesToDisplay.includes(col.macroCategory)) || []}
                                    productsSecondaryDataState={productsSecondaryDataState}
                                    scoreDistributionSelectedVariable={scoreDistributionSelectedVariableAdditional}
                                    moduleName="secondary"
                                />
                            </div>
                            <div
                                style={{
                                    marginLeft: "1.5rem",
                                }}
                            >
                                <DataPointSampling
                                    likertScaleData={likertScaleData}
                                    scoreDistributionGroups={scoreDistributionGroupsAdditional}
                                    scoreDistributionSelectedVariable={scoreDistributionSelectedVariableAdditional}
                                    moduleName="secondary"
                                    data={data}
                                    setData={setData}
                                />
                            </div>
                        </>
                    )}
                </div>

                <div>
                    <SignificanceToggleComponent disabled={isScoreDistributionMode || !isTableShown} />
                    <FilterAcrossProductsToggleComponent />
                    <CohensDButton
                        analysisLevelData={{
                            moduleName: "secondary",
                            dataMappingData: dataMappingData?.columns?.filter((col) => categoriesToDisplay.includes(col.macroCategory)) || [],
                            analysisTypeValue: analysisTypeValue,
                        }}
                        tabData={{
                            productsSecondaryDataState: productsSecondaryDataState,
                        }}
                        setCohensDResult={setCohensDResult}
                        disabled={isScoreDistributionMode || (!isTableShown && selectedStudy.length === 1 && analysisTypeValue.value === "mean")}
                    />
                    <TTestDialog
                        variableCategory="Fragrance Metrics Analysis,JAR"
                        module = "clt"
                        analysisLevelData={{
                            moduleName: "secondary",
                            analysisTypeValue: analysisTypeValue,
                            boxAnalysisTypeSelection: boxAnalysisTypeSelection,
                            boxAnalysisSelection: fragranceAdditionalBoxAnalysisSelection,
                            mappings: sortedItems,
                        }}
                        functionsSettings={{
                            setIsFisher: setIsFisher,
                            setLegends: setLegends,
                            setTTestResult: setTTestResult,
                            setTestLabel: setTestLabel,
                            clearResult: clearResult,
                            // onTTestDataReceived: onTTestDataReceived,
                        }}
                        tabData={{
                            productsSecondaryDataState: productsSecondaryDataState,
                        }}
                        isJarAnalysis={isJarAnalysis}
                        tTestBtnDisabled={isScoreDistributionMode}
                    />
                    <Reset clearTTestResult={clearResult} />
                </div>
            </div>
            <div
                style={{
                    minHeight: "calc(100vh - 300px)",
                }}
            >
                {isTableShown ? (
                    <AnalysisTableTransposed
                        productsSecondaryData={productsSecondaryDataState}
                        handleFilterSubmit={getAditionalResult}
                        selectedStudy={selectedStudy}
                        analysisLevelData={{
                            tableConfig: {
                                stickyColumns: ["Products", "Sample Size", "Consumer Filters"],
                            },
                            moduleName: moduleName,
                            bottomLabelModuleName: "fragranceMetrics",
                            analysisTypeValue: analysisTypeValue,
                            boxAnalysisTypeSelection: boxAnalysisTypeSelection,
                            boxAnalysisSelection: fragranceAdditionalBoxAnalysisSelection,
                            mappings: sortedItems,
                            // hideVariable: "R_OO_Frag_10pt",
                            cohensDResult: cohensDResult,
                        }}
                        tTest={{
                            tTestLegends: legends,
                            isFisher: isFisher,
                            tTestResult: tTestResult,
                            testLabel: testLabel,
                        }}
                        isTableShown={isTableShown}
                        pageContext={pageContext}
                    />
                ) : (
                    <AnalysisTransposedTableChart
                        productsSecondaryData={productsSecondaryDataState}
                        handleFilterSubmit={getAditionalResult}
                        selectedStudy={selectedStudy}
                        analysisLevelData={{
                            tableConfig: {
                                stickyColumns: ["Products", "Sample Size", "Consumer Filters"],
                            },
                            moduleName: moduleName,
                            bottomLabelModuleName: "fragranceMetrics",
                            analysisTypeValue: analysisTypeValue,
                            boxAnalysisTypeSelection: boxAnalysisTypeSelection,
                            boxAnalysisSelection: fragranceAdditionalBoxAnalysisSelection,
                            mappings: sortedItems,
                            // hideVariable: "R_OO_Frag_10pt",
                            isScoreDistributionShown: isScoreDistributionShownAdditional,
                            scoreDistributionGroups: scoreDistributionGroupsAdditional,
                            scoreDistributionSelectedVariable: scoreDistributionSelectedVariableAdditional,
                        }}
                        tTest={{
                            tTestLegends: legends,
                            isFisher: isFisher,
                            tTestResult: tTestResult,
                            testLabel: testLabel,
                        }}
                        isTableShown={isTableShown}
                    />
                )}
            </div>
            {isUnileverFragranceHouse && !isTableShown && selectedStudy.length === 1 && analysisTypeValue.value === "mean" && (
                <div
                    style={{
                        width: "200px",
                        marginTop: "12px",
                    }}
                >
                    <ViewToggle
                        value={isScoreDistributionShownAdditional}
                        setValue={(value) => dispatch(loadIsScoreDistributionShownAdditionalState(value))}
                        labels={["Mean", "% Score Distribution"]}
                    />
                </div>
            )}
            {cohensDResult.length > 0 && <p style={{ fontSize: "12px", marginTop: "6px" }}>Note: Cohen&apos;s d is not visually represented in the chart view</p>}
        </>
    );
}

// Typechecking props for the SecondaryAttributeTab
AditionalAttributeTab.propTypes = {
    dataMappingData: PropTypes.object,
    // onTTestDataReceived: PropTypes.func,
    pageContext: PropTypes.string,
};
export default AditionalAttributeTab;
 